// @ts-nocheck
import { defineStore } from 'pinia'
import { firestoreService } from '@/utils/firestoreService'
import { db } from '@/firebase/config'
import { getDoc, doc } from 'firebase/firestore'
import type { WhereFilterOp } from 'firebase/firestore'
import superjson from 'superjson'
import localforage from 'localforage'
import { Logger } from '@/utils/logger'
import { useDebugStore } from '@/stores/debugStore'

// Create a DocStore logger with the name "DocStore"
const docStoreLogger = new Logger('DocStore')

// Debug tracking utility for baseDocStore operations
const trackDebugOperation = (
  method: string,
  collectionName: string,
  documentId?: string,
  data?: unknown,
  source?: 'firestore' | 'cache' | 'local',
): void => {
  const isDebugMode = import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true'
  if (isDebugMode) {
    try {
      const debugStore = useDebugStore()
      debugStore.addOperation({
        operation: `${method} operation on ${collectionName}${source ? ` (${source})` : ''}`,
        collection: collectionName,
        documentId,
        data,
        method: method as any,
      })
    } catch (error) {
      // Silently fail if debug store is not available
      console.warn('Failed to track debug operation:', error)
    }
  }
}

// Initialize localForage
const itemsStore = localforage.createInstance({
  name: 'scrapboss-items',
  storeName: 'items',
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
  description: 'Persistent store for items',
})

const processedItemsStore = localforage.createInstance({
  name: 'scrapboss-processed-items',
  storeName: 'processed-items',
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
  description: 'Persistent store for processed items',
})

// Helper functions for serialization
const serialize = (data: unknown) => {
  const startTime = performance.now()
  const serialized = superjson.stringify(data)
  const endTime = performance.now()
  const duration = endTime - startTime
  docStoreLogger.debug(
    `Serialization completed: ${serialized.length} bytes in ${duration.toFixed(2)}ms`,
  )
  return serialized
}

const deserialize = <T>(data: string | null): T | null => {
  if (!data) {
    docStoreLogger.debug('No data to deserialize')
    return null
  }
  try {
    const startTime = performance.now()
    const deserialized = superjson.parse(data) as T
    const endTime = performance.now()
    const duration = endTime - startTime

    // Count records if it's an array
    const recordCount = Array.isArray(deserialized) ? deserialized.length : 1

    docStoreLogger.debug(
      `Deserialization completed: ${recordCount} records in ${duration.toFixed(2)}ms`,
    )
    return deserialized
  } catch (error) {
    docStoreLogger.error('Error deserializing data:', error)
    return null
  }
}

export interface BaseEntity {
  id?: string
  createdAt?: Date
  updatedAt?: Date
}

export interface ModelHelpers<T> {
  create: (data?: Partial<T>) => T
  normalize?: (data: unknown) => T
  validate?: (entity: T) => boolean
  [key: string]: unknown // allow additional helpers
}

export interface BaseState<T extends BaseEntity> {
  items: T[]
  processedItems: T[]
  currentItem: T | null
  currentIndex: number
  loading: boolean
  error: string | null
  filters: Record<string, { value: unknown; matchMode: string }>
  initialFilters: Record<string, { value: unknown; matchMode: string }>
  sort: { field: string | null; order: number | null }
  initialSort: { field: string | null; order: number | null }
  initialized: boolean
  lastLoadedAt: Date | null
  staleCheckCache: { result: StaleCheckResult; timestamp: number } | null
}

// --- defineDocStore types
export interface DocStoreOptions<T extends BaseEntity> {
  entityName: string
  collection: string
  model: ModelHelpers<T>
  initialState?: {
    filters?: Record<string, { value: unknown; matchMode: string }>
    sort?: { field: string | null; order: number | null }
  }
}

export interface StaleCheckResult {
  isStale: boolean
  staleCount: number
}

export interface DifferenceItem {
  field: string
  local: unknown
  server: unknown
}

export interface CompareDifference {
  isStale: boolean
  differences: DifferenceItem[]
}

export interface AllStaleConditionsResult {
  count: StaleCheckResult
  newer: StaleCheckResult | null
  current: CompareDifference | null
}

export type DocStore<T extends BaseEntity> = BaseState<T> & {
  setCurrentItem: (id: string) => void
  setProcessedItems: (items: T[]) => void
  clear: () => void
  selectBy: (predicate: (item: T) => boolean) => void
  findBy: (predicate: (item: T) => boolean) => T[]
  fetchById: (id: string) => Promise<T | null>
  load: (force?: boolean) => Promise<void>
  reload: () => Promise<void>
  refresh: (id: string) => Promise<void>
  get: (id: string) => Promise<T | undefined>
  add: (data: Partial<T>) => Promise<T>
  update: (id: string, updates: Partial<T>) => Promise<void>
  remove: (id: string) => Promise<void>
  query: (
    conditions: Array<{ field: string; op: WhereFilterOp; value: unknown }>,
    orderBy?: [string, 'asc' | 'desc'],
    limit?: number,
  ) => Promise<T[]>
  checkStale: () => Promise<StaleCheckResult>
  isCurrentItemStale: () => Promise<StaleCheckResult | null>
  compareCurrentWithServer: () => Promise<CompareDifference | null>
  checkAllStaleConditions: () => Promise<AllStaleConditionsResult>
  setFilters: (filters: Record<string, { value: unknown; matchMode: string }>) => void
  resetFilters: () => void
  setSort: (field: string | null, order: number | null) => void
  resetSort: () => void
  next: () => void
  previous: () => void
}

// --- Full flattened defineDocStore
export function defineDocStore<T extends BaseEntity>(options: DocStoreOptions<T>) {
  const defaultFilters = options.initialState?.filters || {
    global: { value: null, matchMode: 'contains' },
  }

  const defaultSort = options.initialState?.sort || { field: null, order: null }

  return defineStore(options.entityName, {
    state: (): BaseState<T> => ({
      items: [],
      processedItems: [],
      currentItem: null,
      currentIndex: 0,
      loading: false,
      error: null,
      filters: JSON.parse(JSON.stringify(defaultFilters)),
      initialFilters: JSON.parse(JSON.stringify(defaultFilters)),
      sort: JSON.parse(JSON.stringify(defaultSort)),
      initialSort: JSON.parse(JSON.stringify(defaultSort)),
      initialized: false,
      lastLoadedAt: null,
      staleCheckCache: null,
    }),

    getters: {
      nextItem: (state): T | null => state.processedItems[state.currentIndex + 1] || null,
      previousItem: (state): T | null => state.processedItems[state.currentIndex - 1] || null,
      hasNext: (state): boolean => state.currentIndex < state.processedItems.length - 1,
      hasPrevious: (state): boolean => state.currentIndex > 0,
    },

    actions: {
      async load(force = false) {
        const storeLogger = docStoreLogger.child(options.entityName)
        storeLogger.debug('load() called', { force, collection: options.collection })

        const startTime = performance.now()

        // If forcing reload, go straight to Firestore
        if (force) {
          storeLogger.debug('Force loading from Firestore', { collection: options.collection })
          await this.loadFromFirestore()
          const endTime = performance.now()
          const duration = endTime - startTime
          storeLogger.debug('Load completed', {
            duration: `${duration.toFixed(2)}ms`,
            source: 'firestore',
          })
          return
        }

        // Try to load from cache first
        try {
          storeLogger.debug('Loading store from cache', { collection: options.collection })
          const cachedItemsStr = await itemsStore.getItem<string>(options.collection)
          const cachedProcessedItemsStr = await processedItemsStore.getItem<string>(
            options.collection,
          )

          storeLogger.debug('Cached items from localForage', {
            items: cachedItemsStr ? 'present' : 'missing',
            processedItems: cachedProcessedItemsStr ? 'present' : 'missing',
          })

          const cachedItems = deserialize<T[]>(cachedItemsStr)
          const cachedProcessedItems = deserialize<T[]>(cachedProcessedItemsStr)

          if (cachedItems && cachedItems.length > 0) {
            storeLogger.debug('Loaded items from cache', {
              count: cachedItems.length,
              collection: options.collection,
            })
            this.items = cachedItems
            this.processedItems = cachedProcessedItems || cachedItems
            this.initialized = true
            this.lastLoadedAt = new Date()

            // Now check if the cached data is stale
            try {
              const staleCheck = await this.checkStale()
              if (staleCheck.isStale) {
                storeLogger.debug('Cached data is stale, reloading', {
                  collection: options.collection,
                  staleCount: staleCheck.staleCount,
                })
                await this.loadFromFirestore()
              } else {
                storeLogger.debug('Using cached data', {
                  collection: options.collection,
                  count: cachedItems.length,
                })
              }
            } catch (error) {
              storeLogger.warn('Error checking if data is stale', {
                collection: options.collection,
                error: error instanceof Error ? error.message : String(error),
              })
              // Keep using cached data if stale check fails
            }
          } else {
            storeLogger.debug('No cached items found, loading from Firestore', {
              collection: options.collection,
            })
            await this.loadFromFirestore()
          }
        } catch (error) {
          storeLogger.error('Error loading cached items', {
            error: error instanceof Error ? error.message : String(error),
          })
          await this.loadFromFirestore()
        }

        const endTime = performance.now()
        const duration = endTime - startTime
        storeLogger.info('Load completed', { duration: `${duration.toFixed(2)}ms` })
      },

      setCurrentItem(id: string) {
        const index = this.processedItems.findIndex((item) => item.id === id)
        if (index !== -1) {
          this.currentIndex = index
          this.currentItem = this.processedItems[index] as T
        } else {
          const fallbackIndex = this.items.findIndex((item) => item.id === id)
          if (fallbackIndex !== -1) {
            this.currentItem = this.items[fallbackIndex] as T
          }
        }
      },

      async setProcessedItems(items: T[]) {
        const startTime = performance.now()
        this.processedItems = [...items]
        await processedItemsStore.setItem(options.collection, serialize(items))
        const endTime = performance.now()
        const duration = endTime - startTime
        docStoreLogger.debug(`"setProcessedItems" operation completed in ${duration.toFixed(2)}ms`)
        if (this.currentItem) {
          const newIndex = items.findIndex((item) => item.id === this.currentItem?.id)
          if (newIndex !== -1) {
            this.currentIndex = newIndex
          }
        }
      },

      async clear() {
        const startTime = performance.now()
        docStoreLogger.debug('Clearing store', { entityName: options.entityName })
        this.$reset()
        await itemsStore.removeItem(options.collection)
        await processedItemsStore.removeItem(options.collection)
        const endTime = performance.now()
        const duration = endTime - startTime
        docStoreLogger.debug(`Clear operation completed in ${duration.toFixed(2)}ms`, {
          entityName: options.entityName,
        })
      },

      selectBy(predicate: (item: T) => boolean) {
        const index = this.processedItems.findIndex((item) => predicate(item as T))
        if (index !== -1) {
          this.currentIndex = index
          this.currentItem = this.processedItems[index] as T
        }
      },

      findBy(predicate: (item: T) => boolean): T[] {
        return this.items.filter((item) => predicate(item as T)) as T[]
      },

      async fetchById(id: string): Promise<T | null> {
        const startTime = performance.now()
        try {
          const result = await firestoreService.get<T>(options.collection, id)
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(`FetchById operation completed in ${duration.toFixed(2)}ms`, {
            entityName: options.entityName,
          })

          // Track debug operation for single document retrieval
          if (result) {
            trackDebugOperation('get', options.collection, id, result, 'firestore')
          }

          return result
        } catch (error) {
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `FetchById operation completed (error) in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          throw error
        }
      },

      async reload() {
        return this.load(true)
      },

      async refresh(id: string) {
        const startTime = performance.now()
        try {
          const updated = await firestoreService.get<T>(options.collection, id)
          if (!updated) return

          // Track debug operation for document refresh
          trackDebugOperation('get', options.collection, id, updated, 'firestore')

          const index = this.items.findIndex((item) => item.id === id)
          if (index !== -1) this.items[index] = updated as any
          const processedIndex = this.processedItems.findIndex((item) => item.id === id)
          if (processedIndex !== -1) this.processedItems[processedIndex] = updated as any
          if (this.currentItem?.id === id) {
            this.currentItem = updated
          }
          // Sync to localForage
          await itemsStore.setItem(options.collection, serialize(this.items))
          await processedItemsStore.setItem(options.collection, serialize(this.processedItems))
        } catch (error) {
          docStoreLogger.error(`Error refreshing ${options.collection}/${id}`, {
            error: error instanceof Error ? error.message : String(error),
          })
          this.error = error instanceof Error ? error.message : String(error)
        } finally {
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(`Refresh operation completed in ${duration.toFixed(2)}ms`, {
            entityName: options.entityName,
          })
        }
      },

      async get(id: string): Promise<T | undefined> {
        const startTime = performance.now()

        // First try to find in local store
        const localItem = this.items.find((item) => item.id === id) as T

        // If found locally, check if item might be stale before returning
        if (localItem && this.initialized) {
          try {
            // Check if the specific item might have newer data on the server
            if (localItem.updatedAt) {
              const itemCheck = await firestoreService.query<T>(options.collection, [
                { field: 'id', op: '==', value: id },
                { field: 'updatedAt', op: '>', value: localItem.updatedAt },
              ])

              if (itemCheck.length > 0) {
                // Item is stale, fetch fresh data
                docStoreLogger.debug(
                  `Item ${id} in ${options.collection} is stale, refreshing from server`,
                )
                await this.refresh(id)
                const endTime = performance.now()
                const duration = endTime - startTime
                docStoreLogger.debug(
                  `Get operation completed (stale refresh) in ${duration.toFixed(2)}ms`,
                  { entityName: options.entityName },
                )
                const refreshedItem = this.items.find((item) => item.id === id) as T

                // Track debug operation for document retrieval (refreshed from server)
                if (refreshedItem) {
                  trackDebugOperation('get', options.collection, id, refreshedItem, 'firestore')
                }

                return refreshedItem
              }
            }

            // Item is not stale, return from cache
            const endTime = performance.now()
            const duration = endTime - startTime
            docStoreLogger.debug(`Get operation completed (cached) in ${duration.toFixed(2)}ms`, {
              entityName: options.entityName,
            })

            // Track debug operation for document retrieval (from cache)
            trackDebugOperation('get', options.collection, id, localItem, 'cache')

            return localItem
          } catch (error) {
            docStoreLogger.warn(`Error checking if item ${id} in ${options.collection} is stale`, {
              error: error instanceof Error ? error.message : String(error),
            })
            // On error, return the local item as a fallback
            const endTime = performance.now()
            const duration = endTime - startTime
            docStoreLogger.debug(`Get operation completed (fallback) in ${duration.toFixed(2)}ms`, {
              entityName: options.entityName,
            })

            // Track debug operation for document retrieval (from cache - fallback)
            trackDebugOperation('get', options.collection, id, localItem, 'cache')

            return localItem
          }
        }

        // If not found locally or not initialized, fetch from Firestore
        try {
          const item = await firestoreService.get<T>(options.collection, id)
          if (item) {
            // Track debug operation for document retrieval (from Firestore)
            trackDebugOperation('get', options.collection, id, item, 'firestore')

            // Add to local store
            this.items.push(item as any)
            this.processedItems.push(item as any)
            const endTime = performance.now()
            const duration = endTime - startTime
            docStoreLogger.debug(
              `Get operation completed (from Firestore) in ${duration.toFixed(2)}ms`,
              { entityName: options.entityName },
            )
            return item
          }
        } catch (error) {
          docStoreLogger.error(`Error fetching ${options.collection}/${id}`, {
            error: error instanceof Error ? error.message : String(error),
          })
          this.error = error instanceof Error ? error.message : String(error)
        }

        const endTime = performance.now()
        const duration = endTime - startTime
        docStoreLogger.debug(`Get operation completed (not found) in ${duration.toFixed(2)}ms`, {
          entityName: options.entityName,
        })
        return undefined
      },

      async add(data: Partial<T>): Promise<T> {
        const storeLogger = docStoreLogger.child(options.entityName)
        const startTime = performance.now()
        this.loading = true
        try {
          // Make a copy of data without the id property if it exists
          const { id: _, ...dataWithoutId } = data
          const item = options.model.create(dataWithoutId as Partial<T>)
          const docId = await firestoreService.add(options.collection, item)
          item.id = docId

          // Track debug operation for document creation
          trackDebugOperation('add', options.collection, docId, item, 'firestore')

          this.items.push(item as any)
          this.processedItems.push(item as any)
          // Sync to localForage
          await itemsStore.setItem(options.collection, serialize(this.items))
          await processedItemsStore.setItem(options.collection, serialize(this.processedItems))
          storeLogger.info('Item added successfully', { id: docId })
          return item
        } catch (error) {
          storeLogger.error('Error adding item', {
            error: error instanceof Error ? error.message : String(error),
          })
          this.error = error instanceof Error ? error.message : String(error)
          throw error
        } finally {
          this.loading = false
          const endTime = performance.now()
          const duration = endTime - startTime
          storeLogger.info('Add operation completed', { duration: `${duration.toFixed(2)}ms` })
        }
      },

      async update(id: string, updates: Partial<T>) {
        const storeLogger = docStoreLogger.child(options.entityName)
        const startTime = performance.now()
        this.loading = true
        try {
          const updateData = {
            ...updates,
            updatedAt: new Date(),
          }

          await firestoreService.update(options.collection, id, updateData)

          // Track debug operation for document update
          trackDebugOperation('update', options.collection, id, updateData, 'firestore')

          const index = this.items.findIndex((item) => item.id === id)
          if (index !== -1) {
            this.items[index] = { ...this.items[index], ...updates }
          }
          const processedIndex = this.processedItems.findIndex((item) => item.id === id)
          if (processedIndex !== -1) {
            this.processedItems[processedIndex] = {
              ...this.processedItems[processedIndex],
              ...updates,
            }
          }
          if (this.currentItem?.id === id) {
            this.currentItem = { ...this.currentItem, ...updates }
          }
          // Sync to localForage
          await itemsStore.setItem(options.collection, serialize(this.items))
          await processedItemsStore.setItem(options.collection, serialize(this.processedItems))
          storeLogger.info('Item updated successfully', { id })
        } catch (error) {
          storeLogger.error('Error updating item', {
            id,
            error: error instanceof Error ? error.message : String(error),
          })
          this.error = error instanceof Error ? error.message : String(error)
          throw error
        } finally {
          this.loading = false
          const endTime = performance.now()
          const duration = endTime - startTime
          storeLogger.info('Update operation completed', { duration: `${duration.toFixed(2)}ms` })
        }
      },

      async remove(id: string) {
        const storeLogger = docStoreLogger.child(options.entityName)
        const startTime = performance.now()
        this.loading = true
        try {
          await firestoreService.delete(options.collection, id)

          // Track debug operation for document deletion
          trackDebugOperation('delete', options.collection, id, undefined, 'firestore')

          this.items = this.items.filter((item) => item.id !== id)
          this.processedItems = this.processedItems.filter((item) => item.id !== id)
          // Sync to localForage
          await itemsStore.setItem(options.collection, serialize(this.items))
          await processedItemsStore.setItem(options.collection, serialize(this.processedItems))
          storeLogger.info('Item removed successfully', { id })
        } catch (error) {
          storeLogger.error('Error removing item', {
            id,
            error: error instanceof Error ? error.message : String(error),
          })
          this.error = error instanceof Error ? error.message : String(error)
          throw error
        } finally {
          this.loading = false
          const endTime = performance.now()
          const duration = endTime - startTime
          storeLogger.info('Remove operation completed', { duration: `${duration.toFixed(2)}ms` })
        }
      },

      async query(
        conditions: Array<{ field: string; op: WhereFilterOp; value: unknown }>,
        orderBy?: [string, 'asc' | 'desc'],
        limit?: number,
      ) {
        const startTime = performance.now()
        this.loading = true
        try {
          const items = await firestoreService.query<T>(
            options.collection,
            conditions,
            orderBy,
            limit,
          )
          return items
        } catch (error) {
          docStoreLogger.error(`Error querying ${options.collection}`, {
            error: error instanceof Error ? error.message : String(error),
          })
          this.error = error instanceof Error ? error.message : String(error)
          throw error
        } finally {
          this.loading = false
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(`Query operation completed in ${duration.toFixed(2)}ms`, {
            entityName: options.entityName,
          })
        }
      },

      async checkStale(): Promise<StaleCheckResult> {
        const startTime = performance.now()

        // Check if we have a valid cached result (less than 15 seconds old)
        if (this.staleCheckCache && Date.now() - this.staleCheckCache.timestamp < 15000) {
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(`Stale check completed (cached) in ${duration.toFixed(2)}ms`, {
            entityName: options.entityName,
          })
          return this.staleCheckCache.result
        }

        if (!this.lastLoadedAt) {
          const result = { isStale: true, staleCount: 0 }
          this.staleCheckCache = { result, timestamp: Date.now() }
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `Stale check completed (no lastLoadedAt) in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          return result
        }

        // Get count of documents updated since last load
        const updatedCount = await firestoreService.count(options.collection, [
          { field: 'updatedAt', op: '>', value: this.lastLoadedAt },
        ])

        // Get total count of documents in collection
        const totalCount = await firestoreService.count(options.collection, [])

        // Check if we have all documents locally
        const hasAllDocuments = this.items.length === totalCount

        const result = {
          isStale: updatedCount > 0 || !hasAllDocuments,
          staleCount: updatedCount + (hasAllDocuments ? 0 : totalCount - this.items.length),
        }

        // Cache the result
        this.staleCheckCache = { result, timestamp: Date.now() }

        const endTime = performance.now()
        const duration = endTime - startTime
        docStoreLogger.debug(`Stale check completed in ${duration.toFixed(2)}ms`, {
          entityName: options.entityName,
        })

        return result
      },

      async isCurrentItemStale(): Promise<StaleCheckResult | null> {
        const startTime = performance.now()

        if (!this.currentItem?.updatedAt || !this.currentItem?.id) {
          docStoreLogger.warn('Current item missing updatedAt or id')
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `IsCurrentItemStale operation completed (missing data) in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          return null
        }

        try {
          const newer = await firestoreService
            .query<T>(options.collection, [
              { field: 'updatedAt', op: '>', value: this.currentItem.updatedAt },
              { field: 'id', op: '==', value: this.currentItem.id },
            ])
            .then((items) => items[0])

          const result = {
            isStale: !!newer,
            staleCount: newer ? 1 : 0,
          }

          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `IsCurrentItemStale operation completed in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          return result
        } catch (error) {
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `IsCurrentItemStale operation completed (error) in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          throw error
        }
      },

      async compareCurrentWithServer(): Promise<CompareDifference | null> {
        const startTime = performance.now()

        if (!this.currentItem?.id) {
          docStoreLogger.warn('No current item to compare')
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `CompareCurrentWithServer operation completed (no current item) in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          return null
        }

        try {
          const serverDoc = await getDoc(doc(db, options.collection, this.currentItem.id))
          if (!serverDoc.exists()) {
            const result = {
              isStale: true,
              differences: [{ field: 'document', local: 'exists', server: 'does not exist' }],
            }
            const endTime = performance.now()
            const duration = endTime - startTime
            docStoreLogger.debug(
              `CompareCurrentWithServer operation completed (not exists) in ${duration.toFixed(2)}ms`,
              { entityName: options.entityName },
            )
            return result
          }

          const serverData = serverDoc.data() as T
          const differences: DifferenceItem[] = []
          const fields = new Set([...Object.keys(this.currentItem), ...Object.keys(serverData)])

          for (const field of fields) {
            const currentItemValue = this.currentItem[field as keyof typeof this.currentItem]
            const serverValue = serverData[field as keyof typeof serverData]

            if (JSON.stringify(currentItemValue) !== JSON.stringify(serverValue)) {
              differences.push({
                field,
                local: currentItemValue,
                server: serverValue,
              })
            }
          }

          const result = {
            isStale: differences.length > 0,
            differences,
          }

          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `CompareCurrentWithServer operation completed in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          return result
        } catch (error) {
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `CompareCurrentWithServer operation completed (error) in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          throw error
        }
      },

      async checkAllStaleConditions(): Promise<AllStaleConditionsResult> {
        const startTime = performance.now()

        try {
          const [count, newer, current] = await Promise.all([
            this.checkStale(),
            this.isCurrentItemStale(),
            this.compareCurrentWithServer(),
          ])

          const result = { count, newer, current }
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `CheckAllStaleConditions operation completed in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          return result
        } catch (error) {
          const endTime = performance.now()
          const duration = endTime - startTime
          docStoreLogger.debug(
            `CheckAllStaleConditions operation completed (error) in ${duration.toFixed(2)}ms`,
            { entityName: options.entityName },
          )
          throw error
        }
      },

      setFilters(newFilters: Record<string, { value: unknown; matchMode: string }>) {
        this.filters = newFilters
      },

      resetFilters() {
        this.filters = JSON.parse(JSON.stringify(this.initialFilters))
      },

      setSort(field: string | null, order: number | null) {
        this.sort = { field, order }
      },

      resetSort() {
        this.sort = JSON.parse(JSON.stringify(this.initialSort))
      },

      next() {
        if (this.currentIndex < this.processedItems.length - 1) {
          this.currentIndex++
          this.currentItem = this.processedItems[this.currentIndex] as T
        }
      },

      previous() {
        if (this.currentIndex > 0) {
          this.currentIndex--
          this.currentItem = this.processedItems[this.currentIndex] as T
        }
      },

      // Helper function to load data from Firestore
      async loadFromFirestore() {
        const storeLogger = docStoreLogger.child(options.entityName)
        const startTime = performance.now()

        // Note: We don't log Firestore operations for 'app_logs' collection to avoid circular logging
        if (options.collection !== 'app_logs') {
          storeLogger.debug('Loading items from Firestore', { collection: options.collection })
        }
        this.loading = true
        try {
          const raw = await firestoreService.list<T>(options.collection)

          // Skip logging for app_logs to avoid circular logging
          if (options.collection !== 'app_logs') {
            storeLogger.debug('Retrieved items from Firestore', {
              count: raw.length,
              collection: options.collection,
            })
          }

          const normalizedItems = options.model.normalize
            ? raw.map((item) => options.model.normalize!(item))
            : raw

          // Update state
          this.items = normalizedItems
          this.processedItems = [...normalizedItems]

          // Cache in localForage with serialization
          if (options.collection !== 'app_logs') {
            storeLogger.debug('Caching items in localForage', {
              count: normalizedItems.length,
              collection: options.collection,
            })
          }
          await itemsStore.setItem(options.collection, serialize(normalizedItems))
          await processedItemsStore.setItem(options.collection, serialize(normalizedItems))

          // Verify the data was stored
          const verifyItems = await itemsStore.getItem<string>(options.collection)
          if (options.collection !== 'app_logs') {
            storeLogger.debug('Verification of stored items', {
              status: verifyItems ? 'present' : 'missing',
            })
          }

          this.initialized = true
          this.lastLoadedAt = new Date()
          // Clear stale check cache after successful load
          this.staleCheckCache = null
        } catch (error) {
          // Always log errors, even for app_logs, but use console to avoid circular logging
          if (options.collection === 'app_logs') {
            console.error('Error loading app_logs from Firestore:', error)
          } else {
            storeLogger.error('Error loading from Firestore', {
              error: error instanceof Error ? error.message : String(error),
            })
          }
          this.error = error instanceof Error ? error.message : String(error)
        } finally {
          this.loading = false
          const endTime = performance.now()
          const duration = endTime - startTime

          // Skip completion logging for app_logs to avoid circular logging
          if (options.collection !== 'app_logs') {
            storeLogger.info('LoadFromFirestore completed', {
              duration: `${duration.toFixed(2)}ms`,
            })
          }
        }
      },
    },
    persist: {
      serializer: {
        serialize: superjson.stringify,
        deserialize: superjson.parse,
      },
      omit: ['items', 'processedItems'],
    },
  }) as unknown as () => DocStore<T>
}
